import { useCallback, useMemo } from "react";
import {
  TextInput,
  Textarea,
  Stack,
  Select,
  Group,
  Button,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { Practice } from "../../../services/admin/practices.service";
import { usePathologists } from "../../../hooks/useAdmin";
import {
  scrollToFirstError,
  required,
  phone,
  fax,
  email,
} from "../../../utils/formValidation";
import { PracticeLogoUpload } from "./PracticeLogoUpload";
import { ReportFooterEditor } from "../shared/ReportFooterEditor";

export interface PracticeFormValues {
  name: string;
  shortName: string;
  primaryPhone: string;
  secondaryPhone: string;
  fax: string;
  address: string;
  email: string;
  medicalDirector: string;
  footerHtml: string;
  preferredPathologistId: string;
  isActive: boolean;
  logoS3Key: string | null;
}

interface PracticeFormProps {
  initialValues?: Practice;
  onSubmit: (values: PracticeFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  formId?: string;
  hideFooter?: boolean;
}

export default function PracticeForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel,
  formId,
  hideFooter = false,
}: PracticeFormProps) {
  const { data: pathologists = [] } = usePathologists();
  const pathologistOptions = useMemo(
    () =>
      pathologists
        .map((p) => ({ value: p.id, label: p.full_name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [pathologists]
  );

  const form = useForm<PracticeFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      shortName: initialValues?.shortName || "",
      primaryPhone: initialValues?.primaryPhone || "",
      secondaryPhone: initialValues?.secondaryPhone || "",
      fax: initialValues?.fax || "",
      address: initialValues?.address || "",
      email: initialValues?.email || "",
      medicalDirector: initialValues?.medicalDirector || "",
      footerHtml: initialValues?.footerHtml || "",
      preferredPathologistId: initialValues?.preferredPathologistId || "",
      isActive: initialValues?.isActive ?? true,
      logoS3Key: initialValues?.logoS3Key ?? null,
    },
    validate: {
      name: required("Practice name is required"),
      primaryPhone: phone(),
      secondaryPhone: phone(),
      fax: fax(),
      email: email(),
    },
    validateInputOnBlur: true,
  });

  const handleSubmit = form.onSubmit(
    (values) => onSubmit(values),
    () => scrollToFirstError(form)
  );
  const handleFooterHtmlChange = useCallback(
    (value: string) => form.setFieldValue("footerHtml", value),
    [form]
  );

  return (
    <form noValidate onSubmit={handleSubmit} id={formId}>
      <Stack gap="md">
        <TextInput
          label="Practice Name"
          placeholder="Enter practice name"
          withAsterisk
          {...form.getInputProps("name")}
        />

        <TextInput
          label="Short Name"
          placeholder="Enter abbreviated name (optional)"
          {...form.getInputProps("shortName")}
        />

        <TextInput
          label="Primary Phone"
          placeholder="Enter primary phone number"
          {...form.getInputProps("primaryPhone")}
        />

        <TextInput
          label="Secondary Phone"
          placeholder="Enter secondary phone number"
          {...form.getInputProps("secondaryPhone")}
        />

        <TextInput
          label="Fax"
          placeholder="Enter fax number"
          {...form.getInputProps("fax")}
        />

        <TextInput
          label="Email"
          placeholder="Enter contact email address"
          {...form.getInputProps("email")}
        />

        <Textarea
          label="Address"
          placeholder="Enter physical address"
          autosize
          minRows={2}
          maxRows={4}
          {...form.getInputProps("address")}
        />

        <TextInput
          label="Medical Director"
          placeholder="Enter the report-facing name and credentials (optional)"
          description="Displayed in report headers when this practice is the configured identity source."
          {...form.getInputProps("medicalDirector")}
        />

        <ReportFooterEditor
          value={form.values.footerHtml}
          onChange={handleFooterHtmlChange}
        />

        <Select
          label="Preferred Pathologist"
          placeholder="Select preferred pathologist"
          data={pathologistOptions}
          searchable
          clearable
          {...form.getInputProps("preferredPathologistId")}
        />

        <PracticeLogoUpload
          currentLogoS3Key={form.values.logoS3Key}
          onLogoChange={(key) => form.setFieldValue("logoS3Key", key)}
        />

        {!hideFooter && (onCancel || submitLabel !== undefined) && (
          <Group
            justify={onCancel ? "space-between" : "flex-end"}
            gap="xs"
            mt="md"
          >
            {onCancel && (
              <Button variant="default" onClick={onCancel} type="button">
                Cancel
              </Button>
            )}
            <Button type="submit" loading={isSubmitting}>
              {submitLabel || (initialValues ? "Update" : "Create")}
            </Button>
          </Group>
        )}
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
